'use client';
import { LoadingOutlined } from '@ant-design/icons';
import { GetProp, message, Upload, UploadProps } from 'antd';
import { useEffect, useState } from 'react';
import { Gallery } from 'iconsax-react';
import { AllowedFileTypes } from '../FormField/interfaces';
import { useTranslation } from 'react-i18next';
import HelperText from './HelperText';

export type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

const getBase64 = (img: FileType, callback: (url: string) => void) => {
  const reader = new FileReader();
  reader.addEventListener('load', () => callback(reader.result as string));
  reader.readAsDataURL(img);
};

interface props {
  onChange: (arg: unknown) => void;
  name: string;
  id: string;
  className?: string;
  maxFileSize?: number;
  allowedTypes?: AllowedFileTypes[];
  uploadRef?: React.RefObject<unknown>;
  helperText?: string;
  status?: 'error' | 'success';
  disabled?: boolean;
  fileList?: string | File | null; // This is the value from FormField
}

export default function UploadImage({
  uploadRef,
  allowedTypes = ['*/*'],
  id,
  maxFileSize = 5,
  name,
  onChange,
  className,
  helperText,
  status,
  disabled,
  fileList,
}: props) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string>();

  const beforeUpload = (file: File) => {
    const isValidSize = file.size / 1024 / 1024 <= maxFileSize; // Convert bytes to MB
    if (!isValidSize) {
      message.error(t('components.validations.maxFileSize', { size: maxFileSize }), 5);
      return Upload.LIST_IGNORE;
    }
    if (allowedTypes.includes('*/*')) return true;
    if (!allowedTypes.includes(file.type as AllowedFileTypes)) {
      message.error(
        `${file.name} ${t('components.validations.thisFielTypeNotAcceptable') || t('validations.thisFielTypeNotAcceptable')}`,
        5
      );
      return Upload.LIST_IGNORE; // Prevent the file from being added
    }

    return true; // Allow the file to be added
  };

  const handleChange: UploadProps['onChange'] = (info) => {
    if (info.file.status === 'uploading') {
      setLoading(true);
      return;
    }

    if (info.file.status === 'done' || !info.file.status) {
      onChange?.(info.file);

      // Get this url from response in real world.
      const file = info.file.originFileObj || info.file;
      getBase64(file as FileType, (url) => {
        setLoading(false);
        setImageUrl(url);
      });
    }

    if (info.file.status === 'error') {
      setLoading(false);
    }
  };

  const uploadButton = (
    <button
      type="button"
      className="flex flex-col items-center border-0 bg-none"
    >
      {loading ? (
        <LoadingOutlined />
      ) : (
        <div className="relative">
          <Gallery className="opacity-10 -translate-x-1 -rotate-[10deg]" />

          <Gallery
            className="absolute top-0 translate-x-1 rotate-[30deg] opacity-40"
            variant="Bold"
          />
        </div>
      )}
    </button>
  );

  useEffect(() => {
    if (typeof fileList === 'string') {
      setImageUrl(fileList);
    } else if (fileList instanceof File) {
      getBase64(fileList as FileType, (url) => {
        setImageUrl(url);
      });
    } else if (!fileList) {
      setImageUrl(undefined);
    }
  }, [fileList]);

  return (
    <>
      <Upload
        ref={uploadRef}
        id={id}
        name={name}
        listType="picture-circle"
        className={'avatar-uploader' + ' ' + className}
        showUploadList={false}
        beforeUpload={beforeUpload}
        onChange={handleChange}
        disabled={disabled}
        accept={allowedTypes?.join(',') || "image/jpg,image/png,image/jpeg"}
        customRequest={({ onSuccess }) => {
          setTimeout(() => {
            onSuccess?.('ok');
          }, 0);
        }}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt="avatar"
            style={{ width: '100%', borderRadius: '50%' }}
            className="w-full h-full object-cover"
          />
        ) : (
          uploadButton
        )}
      </Upload>
      {helperText && <HelperText text={helperText} status={status} />}
    </>
  );
}
